import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { initiateLogin, initiateLogout, isLocalDevHost } from '../lib/authentik';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Session management refs
  const refreshIntervalRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isRefreshingRef = useRef(false);
  const refreshQueueRef = useRef([]); // Queue concurrent refresh attempts
  const preemptiveTimeoutRef = useRef(null); // 45-minute preemptive refresh

  // Session refresh with improved retry logic and queueing
  const refreshSession = useCallback(async (retryCount = 0) => {
    // If already refreshing, queue this request
    if (isRefreshingRef.current) {
      return new Promise((resolve) => {
        refreshQueueRef.current.push(resolve);
      });
    }

    isRefreshingRef.current = true;

    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();

      if (error) {
        // Check if it's a network error
        const isNetworkError = error.message?.toLowerCase().includes('network') ||
                              error.message?.toLowerCase().includes('fetch') ||
                              error.message?.toLowerCase().includes('failed to fetch') ||
                              error.code === 'NETWORK_ERROR';

        // Retry with longer exponential backoff for network errors
        if (isNetworkError && retryCount < 5) {
          const delay = Math.min(Math.pow(2, retryCount) * 2000, 32000); // 2s, 4s, 8s, 16s, 32s max

          isRefreshingRef.current = false;
          // Process queued requests
          const queue = refreshQueueRef.current;
          refreshQueueRef.current = [];
          queue.forEach(resolve => resolve());

          await new Promise(resolve => setTimeout(resolve, delay));
          return refreshSession(retryCount + 1);
        }

        // After max retries, just log the error (no warnings - session will keep trying)
        if (retryCount >= 5) {
          console.error('[Auth] Max refresh retries reached - will retry on next interval');
        } else if (!isNetworkError) {
          // Auth error - log but keep trying
          console.error('[Auth] Authentication error during refresh - will retry');
        }

        return null;
      }

      // Update last activity on successful refresh
      lastActivityRef.current = Date.now();
      return session;
    } catch (error) {
      console.error('[Auth] Unexpected error during session refresh:', error);

      // Treat unexpected errors as network errors
      if (retryCount < 5) {
        const delay = Math.min(Math.pow(2, retryCount) * 2000, 32000);

        isRefreshingRef.current = false;
        // Process queued requests
        const queue = refreshQueueRef.current;
        refreshQueueRef.current = [];
        queue.forEach(resolve => resolve());

        await new Promise(resolve => setTimeout(resolve, delay));
        return refreshSession(retryCount + 1);
      } else {
        console.error('[Auth] Max retries reached in catch block - will retry on next interval');
        return null;
      }
    } finally {
      isRefreshingRef.current = false;
      // Process any queued requests
      const queue = refreshQueueRef.current;
      refreshQueueRef.current = [];
      queue.forEach(resolve => resolve());
    }
  }, []);

  // Activity detection
  const handleActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    
    // Clear existing timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    // Refresh session if inactive for more than 20 minutes and user is logged in
    if (user) {
      activityTimeoutRef.current = setTimeout(() => {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity >= 20 * 60 * 1000) { // 20 minutes
          refreshSession();
        }
      }, 20 * 60 * 1000);
    }
  }, [user, refreshSession]);

  // Setup session management
  useEffect(() => {
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        // Check for local dev Authentik user if no Supabase session
        if (!session?.user) {
          if (!isLocalDevHost()) {
            // Self-heal browsers left with an Authentik-only session by a failed
            // SSO callback, so they go back through SSO instead of loading empty
            localStorage.removeItem('authentik_user');
            localStorage.removeItem('authentik_tokens');
          }
          const authentikUserStr = localStorage.getItem('authentik_user');
          if (authentikUserStr) {
            try {
              const authentikUser = JSON.parse(authentikUserStr);
              // Create a pseudo-user object for local dev
              const localDevUser = {
                id: authentikUser.sub,
                email: authentikUser.email,
                user_metadata: {
                  full_name: authentikUser.name,
                  sso_only: true,
                  local_dev: true,
                },
              };
              console.log('[Auth] Using local dev Authentik user:', authentikUser.email);
              setUser(localDevUser);
              setLoading(false);
              return; // Skip Supabase session setup for local dev
            } catch (e) {
              console.error('[Auth] Error parsing authentik_user:', e);
              localStorage.removeItem('authentik_user');
            }
          }
        }

        setUser(session?.user ?? null);

        // Setup session refresh interval if user is logged in
        if (session?.user) {
          // Refresh every 20 minutes (more frequent to prevent expiry)
          refreshIntervalRef.current = setInterval(() => {
            refreshSession();
          }, 20 * 60 * 1000);

          // Pre-emptive refresh at 45 minutes if no recent activity
          preemptiveTimeoutRef.current = setTimeout(() => {
            const timeSinceLastActivity = Date.now() - lastActivityRef.current;
            if (timeSinceLastActivity >= 15 * 60 * 1000) { // 15 minutes of inactivity
              refreshSession();
            }
          }, 45 * 60 * 1000);
        }
      } catch (error) {
        console.error('Error getting session:', error);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Don't override local dev Authentik user with null from Supabase
        if (!session?.user && isLocalDevHost()) {
          const authentikUserStr = localStorage.getItem('authentik_user');
          if (authentikUserStr) {
            // Keep using local dev user - don't set to null
            console.log('[Auth] Supabase session null, but keeping local dev Authentik user');
            setLoading(false);
            return;
          }
        }

        setUser(session?.user ?? null);
        setLoading(false);

        // Clear intervals when signing out
        if (!session?.user) {
          if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
          }
          if (activityTimeoutRef.current) {
            clearTimeout(activityTimeoutRef.current);
            activityTimeoutRef.current = null;
          }
          if (preemptiveTimeoutRef.current) {
            clearTimeout(preemptiveTimeoutRef.current);
            preemptiveTimeoutRef.current = null;
          }
        } else {
          // Setup intervals when signing in
          if (!refreshIntervalRef.current) {
            refreshIntervalRef.current = setInterval(() => {
              refreshSession();
            }, 20 * 60 * 1000); // Every 20 minutes
          }

          // Pre-emptive refresh at 45 minutes
          if (!preemptiveTimeoutRef.current) {
            preemptiveTimeoutRef.current = setTimeout(() => {
              const timeSinceLastActivity = Date.now() - lastActivityRef.current;
              if (timeSinceLastActivity >= 15 * 60 * 1000) {
                refreshSession();
              }
            }, 45 * 60 * 1000);
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      if (preemptiveTimeoutRef.current) {
        clearTimeout(preemptiveTimeoutRef.current);
      }
    };
  }, [refreshSession]);

  // Setup activity listeners
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user, handleActivity]);

  // Visibility change listener - refresh session when user returns to idle tab
  // Browsers throttle timers in background tabs, so this ensures session stays fresh
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        const idleMinutes = Math.round(timeSinceLastActivity / 60000);

        // If tab was idle for more than 5 minutes, refresh session proactively
        if (timeSinceLastActivity > 5 * 60 * 1000) {
          console.log(`[Auth] Tab became visible after ${idleMinutes} minutes idle - refreshing session`);
          await refreshSession();
        }

        // Update last activity since user is now active
        lastActivityRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, refreshSession]);

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[Auth] SignIn error:', error.message);
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('[Auth] SignIn exception:', error);
      return { data: null, error };
    }
  };

  const signUp = async (email, password, fullName, employeeData = null) => {
    try {
      // Use employee role or default to 'user' - NEVER auto-admin
      const userRole = employeeData?.role || 'user';

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: userRole,
            employee_id: employeeData?.id || null
          }
        }
      });

      if (error) {
        console.error('[Auth] SignUp error:', error.message);
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('[Auth] SignUp exception:', error);
      return { data: null, error };
    }
  };

  const signOut = async (useSSO = false) => {
    try {
      // Clear all timers before signing out
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
        activityTimeoutRef.current = null;
      }
      if (preemptiveTimeoutRef.current) {
        clearTimeout(preemptiveTimeoutRef.current);
        preemptiveTimeoutRef.current = null;
      }

      // Clear local dev Authentik user if present
      const isLocalDevUser = user?.user_metadata?.local_dev;
      if (isLocalDevUser) {
        localStorage.removeItem('authentik_user');
        localStorage.removeItem('authentik_tokens');
        setUser(null);
        // For local dev, also logout from Authentik if requested
        if (useSSO) {
          initiateLogout();
        }
        return { error: null };
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }

      // If user logged in via SSO, also log out from Authentik
      if (useSSO || user?.user_metadata?.sso_only) {
        // Redirect to Authentik logout
        initiateLogout();
        return { error: null }; // Won't reach here due to redirect
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  // SSO Login - redirects to Authentik
  const signInWithSSO = async () => {
    try {
      await initiateLogin();
      // This will redirect, so we won't reach here
      return { error: null };
    } catch (error) {
      console.error('[Auth] SSO SignIn error:', error);
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) {
        throw error;
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signInWithSSO,
    signUp,
    signOut,
    resetPassword,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};